<template>
    <div>
        <!-- Number type fields -->
        <template v-if="type === 'number'">
            <div class="grid grid-cols-2 gap-2">
                <input v-model="min" type="number" class="form-input" placeholder="Min" />
                <input v-model="max" type="number" class="form-input" placeholder="Max" />
            </div>
        </template>

        <!-- Text type fields -->
        <template v-if="type === 'text'">
            <div class="grid grid-cols-2 gap-2">
                <input v-model="minlength" type="number" class="form-input" placeholder="Min length" />
                <input v-model="maxlength" type="number" class="form-input" placeholder="Max length" />
            </div>
        </template>

        <!-- File type fields -->
        <template v-if="type === 'file'">
            <div class="grid grid-cols-2 gap-2">
                <input v-model="max_size" type="number" class="form-input" placeholder="Max Size (MB)" />
                <input v-model="accept" type="text" class="form-input" placeholder=".jpg, .jpeg, .png" />
            </div>
        </template>

        <!-- Select, Checkbox, Radio type fields -->
        <template v-if="type === 'select' || type === 'checkbox' || type === 'radio'">
            <select v-model="selectSource" class="form-input w-full mb-2" v-if="type === 'select'">
                <option value="manual">Manual Options</option>
                <option value="api">External API</option>
            </select>

            <!-- For select, checkbox, radio: manual options -->
            <div v-if="selectSource === 'manual' || type === 'checkbox' || type === 'radio'" class="space-y-2">
                <div v-for="(opt, idx) in selectOptions" :key="idx" class="flex items-center gap-2">
                    <input v-model="opt.key" type="text" class="form-input flex-1" placeholder="Option Key" />
                    <input v-model="opt.value" type="text" class="form-input flex-1" placeholder="Option Value" />
                    <button v-if="selectOptions.length > 1" type="button" class="text-red-500 hover:text-red-700"
                        @click="$emit('removeOption', idx)">
                        <i class="fa fa-minus-circle"></i>
                    </button>
                </div>
                <button type="button" class="text-green-600 hover:text-green-800 text-sm flex items-center gap-1"
                    @click="$emit('addOption')">
                    <i class="fa fa-plus-circle"></i>
                    <span>Add Option</span>
                </button>
            </div>

            <!-- For select: API source -->
            <div v-else-if="type === 'select' && selectSource === 'api'" class="space-y-2">
                <input v-model="apiUrl" type="text" class="form-input w-full"
                    placeholder="API URL for options (e.g., /api/choices)" />
            </div>
        </template>

            <!-- Textarea type field -->
            <template v-if="type === 'textarea'">
                <textarea class="form-input w-full" rows="3" disabled placeholder="Textarea field"></textarea>
            </template>

            <!-- Email type field -->
            <template v-if="type === 'email'">
                <input type="email" class="form-input w-full" disabled placeholder="Email field" />
            </template>

            <!-- Phone type field -->
            <template v-if="type === 'phone'">
                <input type="tel" class="form-input w-full" disabled placeholder="Phone number field" />
            </template>

            <!-- URL type field -->
            <template v-if="type === 'url'">
                <input type="url" class="form-input w-full" disabled placeholder="URL field" />
            </template>

            <!-- Color type field -->
            <template v-if="type === 'color'">
                <input type="color" class="form-input w-16 h-10 p-0" disabled />
            </template>

            <!-- Password type field -->
            <template v-if="type === 'password'">
                <input type="password" class="form-input w-full" disabled placeholder="Password field" />
            </template>

            <!-- Hidden type field -->
            <template v-if="type === 'hidden'">
                <input type="hidden" />
                <span class="text-gray-400">Hidden field</span>
            </template>

            <!-- Currency type field -->
            <template v-if="type === 'currency'">
                <input type="text" class="form-input w-full" disabled placeholder="Currency field (e.g., USD)" />
            </template>

            <!-- Other type field -->
            <template v-if="type === 'other'">
                <input type="text" class="form-input w-full" disabled placeholder="Other field type" />
            </template>
    </div>
</template>

<script>
    import { computed } from 'vue'
    import { FIELD_TYPES, SELECT_SOURCES } from '../composables/useApprovalFields'

    export default {
        name: 'FieldTypeInputs',
        props: {
            approval: {
                type: Object,
                required: true
            },
            index: {
                type: Number,
                required: true
            }
        },
        emits: ['update', 'addOption', 'removeOption'],
        setup(props, { emit }) {
            const updateField = (field, value) => {
                emit('update', props.index, field, value)
            }

            // Two-way binding helpers
            const type = computed({
                get: () => props.approval.type,
                set: v => updateField('type', v)
            })

            const min = computed({
                get: () => props.approval.min,
                set: v => updateField('min', v)
            })

            const max = computed({
                get: () => props.approval.max,
                set: v => updateField('max', v)
            })

            const minlength = computed({
                get: () => props.approval.minlength,
                set: v => updateField('minlength', v)
            })

            const maxlength = computed({
                get: () => props.approval.maxlength,
                set: v => updateField('maxlength', v)
            })

            const max_size = computed({
                get: () => props.approval.max_size,
                set: v => updateField('max_size', v)
            })

            const accept = computed({
                get: () => props.approval.accept,
                set: v => updateField('accept', v)
            })

            const selectSource = computed({
                get: () => props.approval.selectSource,
                set: v => updateField('selectSource', v)
            })

            const selectOptions = computed({
                get: () => props.approval.selectOptions,
                set: v => updateField('selectOptions', v)
            })

            const apiUrl = computed({
                get: () => props.approval.apiUrl,
                set: v => updateField('apiUrl', v)
            })

            return {
                type,
                min,
                max,
                minlength,
                maxlength,
                max_size,
                accept,
                selectSource,
                selectOptions,
                apiUrl,
                FIELD_TYPES,
                SELECT_SOURCES
            }
        }
    }
</script>
